import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

import { Button } from "Components/Button";
import Input from "Components/Form/Input";
import Select from "Components/Form/Select";
import PageHeader from "Components/Pages/Header";
import PageWrapper from "Components/Pages/Wrapper";
import { Pagination } from "Components/Pagination";
import Breadcrumb from "Components/Shared/Breadcrumb";
import { Edit, Trash, Eye } from "lucide-react";

import { deleteUnitHoliday, getUnitHolidays } from "Requests/Units";

import validatePermissions from "Utils/validatePermissions";
import { createToast } from "Utils/toastFunc";

import LoadingImage from "Assets/Images/loading-purple.gif";

import type { Store } from "Reducers/interface";
import { formatDateBR } from "Utils/Date";
import { Loader } from "Components/Loader";
import { Box } from "styles/globalComponents";

export type UnitHolidayListItem = {
  id: number;
  description: string;
  holiday: string;
  scope: string;
  is_fixed?: boolean;
  city_id?: number | null;
  city?: string | null;
};

type LaravelHolidayPage = {
  data: UnitHolidayListItem[];
  current_page: number;
  last_page: number;
};

function scopeLabel(scope: string | undefined) {
  if (scope === "national") return "Nacional";
  if (scope === "municipal") return "Municipal";
  if (scope === "state") return "Estadual";
  return scope ?? "—";
}

type HolidayListFilters = {
  description: string;
  holiday: string;
  scope: "" | "national" | "municipal" | "state";
};

type HolidayListFilterParams = Partial<
  Pick<HolidayListFilters, "description" | "holiday" | "scope">
>;

const INITIAL_FILTERS: HolidayListFilters = {
  description: "",
  holiday: "",
  scope: "",
};

const SCOPE_FILTER_OPTIONS = [
  { id: "national", name: "Nacional" },
  { id: "municipal", name: "Municipal" },
  { id: "state", name: "Estadual" },
] as const;

function buildHolidayFilterParams(
  filters: HolidayListFilters
): HolidayListFilterParams {
  const params: HolidayListFilterParams = {};
  const description = filters.description.trim();

  if (description) params.description = description;
  if (filters.holiday) params.holiday = filters.holiday;
  if (filters.scope) params.scope = filters.scope;

  return params;
}

export function UnitsHolidayList() {
  const history = useHistory();
  const userPermissions = useSelector((state: Store) => state.User.permissions);

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<UnitHolidayListItem[]>([]);
  const [pagination, setPagination] = useState<Partial<LaravelHolidayPage>>({});
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [filters, setFilters] = useState<HolidayListFilters>(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<HolidayListFilterParams>(
    {}
  );

  const fetchPage = useCallback(
    async (page: number, filterParams: HolidayListFilterParams = {}) => {
      setLoading(true);
      try {
        const res = await getUnitHolidays(page, filterParams);
        const body = res?.data as LaravelHolidayPage | undefined;
        setItems(body?.data ?? []);
        setPagination({
          current_page: body?.current_page,
          last_page: body?.last_page,
        });
      } catch {
        createToast("error", "Não foi possível carregar os feriados.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  function handleFilterSubmit(event: React.FormEvent) {
    event.preventDefault();
    const filterParams = buildHolidayFilterParams(filters);
    setAppliedFilters(filterParams);
    fetchPage(1, filterParams);
  }

  function handleClearFilters(event: React.MouseEvent) {
    event.preventDefault();
    setFilters(INITIAL_FILTERS);
    setAppliedFilters({});
    fetchPage(1, {});
  }

  function handlePagination(e: {
    currentPage: number;
    totalPages: number;
    pageLimit: number;
    totalRecords: number;
  }) {
    if (pagination.current_page === e.currentPage) return;
    fetchPage(e.currentPage, appliedFilters);
  }

  async function deleteHolidayLayout(id: number) {
    try {
      setDeleteId(id);
      setLoadingDelete(true);
      await deleteUnitHoliday(id);
      createToast("success", "Feriado excluído com sucesso.");
      await fetchPage(pagination.current_page ?? 1, appliedFilters);
    } catch (error) {
      createToast("error", "Não foi possível excluir o feriado.");
    } finally {
      setLoadingDelete(false);
      setDeleteId(null);
    }
  }

  function LoadingRow() {
    return (
      <tr>
        <td colSpan={6} className="text-center">
          <span className="loading-persons">
            <img src={LoadingImage} alt="" />
          </span>
        </td>
      </tr>
    );
  }

  const canMutate = validatePermissions("Update Units", userPermissions);

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  return (
    <PageWrapper className="entry-app-content">
      <div className="content-holder">
        <div className="page-heading">
          <div className="wrapper-header-content">
            <PageHeader title="Disponibilidade">
              <Breadcrumb
                list={[
                  {
                    path: "/unidades",
                    label: "Unidades",
                    current: false,
                  },
                  {
                    path: "#",
                    label: "Disponibilidade",
                    current: true,
                  },
                ]}
              />
            </PageHeader>
            {canMutate && (
              <div className="buttons-container">
                <Button
                  type="button"
                  borderRadius="111"
                  onClick={() => history.push("/unidades/disponibilidade/novo")}
                >
                  Incluir feriado
                </Button>
              </div>
            )}
          </div>
          <div className="order-home-filter full-width">
            <form className="filters-order" onSubmit={handleFilterSubmit}>
              <Box display="flex" gap="10">
                <div className="sm-4">
                  <Input
                    id="holiday-description-filter"
                    type="text"
                    placeholder="Buscar por descrição"
                    value={filters.description}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const description = event.target.value;
                      setFilters((prev) => ({ ...prev, description }));
                    }}
                    error=""
                    label=""
                    mask=""
                    maskChar=""
                    maxLength={200}
                    min={undefined}
                    multiline={false}
                    onBlur={undefined}
                    onFocus={undefined}
                    onKeyPress={undefined}
                    purple={false}
                    required={false}
                    small={false}
                    readOnly={false}
                    description=""
                  />
                </div>
                <div className="sm-2">
                  <Input
                    id="holiday-date-filter"
                    type="date"
                    placeholder=""
                    value={filters.holiday}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const holiday = event.target.value;
                      setFilters((prev) => ({ ...prev, holiday }));
                    }}
                    error=""
                    label=""
                    mask=""
                    maskChar=""
                    maxLength={undefined}
                    min={undefined}
                    multiline={false}
                    onBlur={undefined}
                    onFocus={undefined}
                    onKeyPress={undefined}
                    purple={false}
                    required={false}
                    small={false}
                    readOnly={false}
                    description=""
                  />
                </div>
                <div className="sm-2">
                  <Select
                    id="holiday-scope-filter"
                    placeholder="Escopo"
                    optionName="name"
                    options={[...SCOPE_FILTER_OPTIONS]}
                    readOnly={false}
                    value={filters.scope}
                    label=""
                    error=""
                    onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                      const scope = event.target
                        .value as HolidayListFilters["scope"];
                      setFilters((prev) => ({ ...prev, scope }));
                    }}
                  />
                </div>
                <Box display="flex" gap="10">
                  <Button type="submit">Buscar</Button>
                  <Button type="button" onClick={handleClearFilters} outlined>
                    Limpar
                  </Button>
                </Box>
              </Box>
            </form>
          </div>
        </div>

        <div className="page-content page-content-list">
          <table className="table-content small-separate details">
            <thead>
              <tr>
                <th>Descrição</th>
                <th>Data</th>
                <th>Escopo</th>
                <th className="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && <LoadingRow />}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-purple">
                    Nenhum feriado cadastrado.
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((row) => (
                  <tr key={row.id}>
                    <td>{row.description}</td>
                    <td>{formatDateBR(row.holiday)}</td>
                    <td>{scopeLabel(row.scope)}</td>
                    <td className="text-right table-action centered">
                      <div className="flex-buttons">
                        <Button
                          type="button"
                          textButton
                          onClick={() =>
                            history.push(`/unidades/disponibilidade/${row.id}`)
                          }
                        >
                          <Eye size={20} />
                        </Button>
                        {canMutate && (
                          <>
                            <Button
                              type="button"
                              textButton
                              onClick={() =>
                                history.push(
                                  `/unidades/disponibilidade/${row.id}/editar`
                                )
                              }
                            >
                              <Edit size={20} />
                            </Button>
                            <Button
                              type="button"
                              textButton
                              onClick={() => deleteHolidayLayout(row.id)}
                            >
                              {loadingDelete && deleteId === row.id ? (
                                <Loader size={20} />
                              ) : (
                                <Trash size={20} />
                              )}
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="pagination-wrapper">
        {!loading && (
          <Pagination
            totalRecords={pagination?.last_page}
            currentPageSelected={pagination?.current_page}
            pageLimit={1}
            pageNeighbours={1}
            onPageChanged={(e) => handlePagination(e)}
          />
        )}
      </div>
    </PageWrapper>
  );
}
